import axios, { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import { serviceUrls } from '../config/serviceUrls';
import {
  AuthSessionExpiredHandledError,
  forceLogoutAndRedirect,
  isLogoutRedirectInProgress,
  isUnauthorizedApiResponse,
  rejectWithAuthSessionExpired,
} from '../utils/authSession';
import { getAccessToken, getRefreshToken, setTokens } from '../utils/token';

interface RefreshTokenResponse {
  access_token: string;
  refresh_token: string;
  token_type: string;
}

type FailedRequest = {
  resolve: (token?: string) => void;
  reject: (reason?: unknown) => void;
};

const refreshAuthToken = async (refreshToken: string): Promise<RefreshTokenResponse> => {
  const refreshClient = axios.create({
    baseURL: serviceUrls.gateway,
    headers: {
      'Content-Type': 'application/json',
    },
  });

  const response = await refreshClient.post<RefreshTokenResponse>('/auth/refresh', {
    refresh_token: refreshToken,
  });
  return response.data;
};

function requestSkipsAuthRedirect(config: InternalAxiosRequestConfig): boolean {
  const headers = config.headers as Record<string, unknown> | undefined;
  return !!(
    headers &&
    (headers['x-skip-auth-redirect'] ||
      headers['X-Skip-Auth-Redirect'] ||
      headers['x-skip-auth-redirect'.toLowerCase()])
  );
}

export const attachAuthInterceptors = (instance: AxiosInstance) => {
  instance.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
      if (isLogoutRedirectInProgress()) {
        return Promise.reject(new AuthSessionExpiredHandledError());
      }

      const token = getAccessToken();
      if (token && config.headers) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      if (config.headers) {
        const role = localStorage.getItem('X_ROLE');
        const tenantId = localStorage.getItem('X_TENANT_ID');
        if (role) config.headers['X-Role'] = role;
        if (tenantId) config.headers['X-Tenant-Id'] = tenantId;
      }
      return config;
    },
    (error) => Promise.reject(error)
  );

  let isRefreshing = false;
  let failedQueue: FailedRequest[] = [];

  const processQueue = (error: AxiosError | null, token: string | null = null) => {
    failedQueue.forEach((prom) => {
      if (error) {
        prom.reject(error);
      } else {
        prom.resolve(token || undefined);
      }
    });
    failedQueue = [];
  };

  instance.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
      if (error instanceof AuthSessionExpiredHandledError) {
        return Promise.reject(error);
      }

      if (isLogoutRedirectInProgress()) {
        return rejectWithAuthSessionExpired();
      }

      const originalRequest = (error.config || {}) as InternalAxiosRequestConfig & {
        _retry?: boolean;
      };
      const status = error.response?.status ?? 0;
      const responseData = error.response?.data;

      if (
        isUnauthorizedApiResponse(status, responseData) &&
        !originalRequest.url?.includes('/auth/refresh') &&
        !requestSkipsAuthRedirect(originalRequest)
      ) {
        if (isRefreshing) {
          return new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject });
          })
            .then((token) => {
              if (originalRequest.headers && token) {
                originalRequest.headers.Authorization = `Bearer ${token}`;
              }
              return instance(originalRequest);
            })
            .catch((err) => Promise.reject(err));
        }

        if (!originalRequest._retry) {
          originalRequest._retry = true;
          const refreshToken = getRefreshToken();

          if (!refreshToken) {
            return rejectWithAuthSessionExpired();
          }

          isRefreshing = true;
          try {
            const { access_token, refresh_token } = await refreshAuthToken(refreshToken);
            setTokens(access_token, refresh_token);
            processQueue(null, access_token);
            return instance({
              ...originalRequest,
              headers: {
                ...originalRequest.headers,
                Authorization: `Bearer ${access_token}`,
              },
            });
          } catch (refreshError) {
            const refreshAxios = refreshError as AxiosError;
            const refreshStatus = refreshAxios.response?.status ?? 0;
            const refreshData = refreshAxios.response?.data;
            processQueue(refreshAxios, null);

            if (
              isUnauthorizedApiResponse(refreshStatus, refreshData) ||
              refreshStatus === 401
            ) {
              return rejectWithAuthSessionExpired();
            }

            return Promise.reject(refreshError);
          } finally {
            isRefreshing = false;
          }
        }

        return rejectWithAuthSessionExpired();
      }

      if (error.response?.status === 403) {
        console.warn('Forbidden request detected');
      }

      return Promise.reject(error);
    }
  );
};
